import { useQuery } from '@tanstack/react-query'
import { useEffect, useId, useState } from 'react'

import { CacheKeys } from '../../../../lib/cache'
import {
  type Claim,
  ClaimPhotoExtractionStatus,
  fetchClaim,
  fetchClaimPhotoExtraction,
} from '../../../../models/claim'

const POLL_MS = 3_000
// Stop asking eventually, e.g. when no worker ever picks the read up.
const POLL_LIMIT_MS = 10 * 60_000

/** Which `photoExtractionFilled` keys belong to each wizard step. */
export const FILLED_PREFIXES = {
  equipment: ['truck', 'trailer', 'driver'],
  incident: ['incident.'],
  others: ['other_vehicle.', 'police.', 'injuries.'],
} as const

export function filledFromPhotos(claim: Claim | undefined, prefixes: readonly string[]): boolean {
  return (claim?.photoExtractionFilled ?? []).some((key) => prefixes.some((prefix) => key.startsWith(prefix)))
}

/** Polls while the uploads are read; `extracted` is the claim once they are. */
export function useClaimPhotoExtraction(claim: Claim | undefined, orgId?: string) {
  const pending = claim?.photoExtractionStatus === ClaimPhotoExtractionStatus.PENDING
  const [timedOut, setTimedOut] = useState(false)
  // Per mount: a claim cached by an earlier visit to this step predates what was saved since.
  const mountId = useId()

  useEffect(() => {
    if (!pending) return
    const timer = setTimeout(() => setTimedOut(true), POLL_LIMIT_MS)
    return () => clearTimeout(timer)
  }, [pending])

  const { data: progress } = useQuery({
    queryKey: [CacheKeys.CLAIMS, claim?.id, orgId, 'photo-extraction', mountId],
    queryFn: () => fetchClaimPhotoExtraction(claim!.id, orgId),
    enabled: pending && !timedOut,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      !query.state.data || query.state.data.photoExtractionStatus === ClaimPhotoExtractionStatus.PENDING
        ? POLL_MS
        : false,
  })
  const finished = !!progress && progress.photoExtractionStatus !== ClaimPhotoExtractionStatus.PENDING

  const { data: extracted } = useQuery({
    queryKey: [CacheKeys.CLAIMS, claim?.id, orgId, 'photo-extraction-claim', mountId],
    queryFn: () => fetchClaim(claim!.id, orgId),
    enabled: pending && finished,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })

  return {
    extracted: pending ? extracted : undefined,
    reading: pending && !extracted && !timedOut,
    latest: (pending && extracted) || claim,
  }
}
